import { useEffect, Fragment } from 'react'
import {
  Toolbar,
  Divider,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Grid,
  Box,
  Tooltip,
} from '@mui/material'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import PropTypes from 'prop-types'
import { useSelector, useDispatch } from 'react-redux'
import { search } from 'store/academic/parent/parent-student'
import { hasPermission } from '../../store'
import { useSession } from 'components/Providers'
import SettingsIcon from '@mui/icons-material/Settings'
import PermissionIcon from '@mui/icons-material/LocalPoliceOutlined'
import NotificationIcon from '@mui/icons-material/NotificationsNoneOutlined'
import { logout } from '../../store/session'
import ParentStudents from '../ParentStudents'
import { useTranslation } from 'react-i18next'
import { useTheme } from '@emotion/react'
import JotformFeedback from './JotFormParentFeedback'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import { LogoutIcon } from '../Icons'
import { providerMenu, schoolMenu, academicMenu, studentMenu } from './MenuItems'
import { launchExternalForm } from 'utils/toLegacyApp'
import * as snackbar from 'utils/snackbar'

const MENU_HEIGHTS = {
  TOOLBAR: 150,
  HEADER: 50,
  FOOTER: 50,
  PADDING: 30,
}

const LOGO_SIZES = {
  EXPANDED: 150,
  COLLAPSED: 30,
}

function MenuItem({ page, location, isMenuCollapsed, sx, ...props }) {
  const { t } = useTranslation()
  const isExternalForm = Boolean(page.externalForm)
  const isExternalLink = Boolean(page.location)
  const isExternal = isExternalForm || isExternalLink
  const isSelected =
    !isExternal &&
    ((location.pathname === '/' && page.path === '/') ||
      (page.path !== '/' && location.pathname.startsWith(page.path)))

  // externalForm → POST a hidden form to a legacy system in a new tab;
  // location → plain external link in a new tab; otherwise navigate normally.
  const buttonProps = isExternalForm
    ? { onClick: () => launchExternalForm(page.externalForm).catch(() => snackbar.error('Unable to open the external system.')) }
    : isExternalLink
      ? { component: 'a', href: page.location, target: '_blank', rel: 'noopener noreferrer' }
      : { component: Link, to: page.path, selected: isSelected }

  return (
    <ListItem key={page.path ?? page.title} disablePadding {...props}>
      <ListItemButton sx={sx} {...buttonProps}>
        {isMenuCollapsed ? (
          <Tooltip title={t(page.title)} placement="right" arrow>
            <ListItemIcon>{page.icon}</ListItemIcon>
          </Tooltip>
        ) : (
          <>
            {page.icon && <ListItemIcon>{page.icon}</ListItemIcon>}
            <ListItemText inset={!page.icon} primary={page.title} />
            {isExternal && <OpenInNewIcon fontSize="small" sx={{ opacity: 0.5 }} />}
          </>
        )}
      </ListItemButton>
    </ListItem>
  )
}

MenuItem.propTypes = {
  page: PropTypes.object.isRequired,
  location: PropTypes.object.isRequired,
  isMenuCollapsed: PropTypes.bool.isRequired,
  sx: PropTypes.object,
}

function ActionMenuItem({ icon: Icon, label, onClick, isMenuCollapsed, dataMenu }) {
  const { t } = useTranslation()

  return (
    <ListItem data-menu={dataMenu} disablePadding>
      <ListItemButton onClick={onClick}>
        {isMenuCollapsed ? (
          <Tooltip title={t(label)} placement="right" arrow>
            <ListItemIcon>
              <Icon />
            </ListItemIcon>
          </Tooltip>
        ) : (
          <>
            <ListItemIcon>
              <Icon />
            </ListItemIcon>
            <ListItemText primary={t(label)} />
          </>
        )}
      </ListItemButton>
    </ListItem>
  )
}

ActionMenuItem.propTypes = {
  icon: PropTypes.elementType.isRequired,
  label: PropTypes.string.isRequired,
  onClick: PropTypes.func.isRequired,
  isMenuCollapsed: PropTypes.bool.isRequired,
  dataMenu: PropTypes.string,
}

function Logo({ isMenuCollapsed, logos, onClick }) {
  if (isMenuCollapsed) {
    return logos ? (
      <Box py={2} display="flex" alignItems="center" justifyContent="center">
        <img
          onClick={onClick}
          src={logos.circle}
          alt="Logo"
          style={{ width: LOGO_SIZES.COLLAPSED, cursor: 'pointer' }}
        />
      </Box>
    ) : null
  }

  return (
    <Box py={1} display="flex" flexDirection="column" alignItems="center" justifyContent="center">
      {logos ? (
        <img
          onClick={onClick}
          src={logos.white}
          alt="Logo"
          style={{ width: LOGO_SIZES.EXPANDED, cursor: 'pointer' }}
        />
      ) : (
        <img
          onClick={onClick}
          src="https://sis-main-files.s3.us-east-1.amazonaws.com/themes/logos/iLearn-Logo-White.png"
          alt="Logo"
          style={{ width: LOGO_SIZES.EXPANDED, cursor: 'pointer' }}
        />
      )}
    </Box>
  )
}

Logo.propTypes = {
  isMenuCollapsed: PropTypes.bool.isRequired,
  logos: PropTypes.object,
  onClick: PropTypes.func.isRequired,
}

function SchoolName({ name, onClick }) {
  return name ? (
    <Typography
      variant="h6"
      component="a"
      onClick={onClick}
      sx={{
        pb: 1,
        pt: 1,
        display: 'flex',
        flexGrow: 1,
        fontWeight: 500,
        color: 'primary.contrastText',
        textDecoration: 'none',
        cursor: 'pointer',
      }}
    >
      {name}
    </Typography>
  ) : null
}

SchoolName.propTypes = {
  name: PropTypes.string,
  onClick: PropTypes.func.isRequired,
}

export default function AppMenu({ isMenuCollapsed }) {
  const location = useLocation()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { logos } = useTheme()
  const session = useSession()

  const { district, school, campus, person } = session
  const studentList = useSelector(state => state.academic.student.list)
  const courseSessionList = useSelector(state => state.academic.courseSession.list)
  const mentorships = useSelector(state => state.mentorship.mentorship.list)
  const serviceCampus = useSelector(state => state.lookup.serviceCampus)

  useEffect(() => {
    if (!studentList && person?.type === 'parent') {
      dispatch(search())
    }
  }, [dispatch, person?.type, studentList])

  const isServiceProvider = district?.isServiceProvider
  const isStaff = person?.type === 'staff'
  const isParent = person?.type === 'parent'
  const isStudent = person?.type === 'student'
  const isStudentOrParent = isStudent || isParent

  const shouldShowAcademicMenu = !isServiceProvider && isStaff && courseSessionList?.length > 0
  const shouldShowMentorshipMenu = mentorships?.length === 1 && mentorships[0].menteeId === person?.id && !mentorships[0].endDate 
  const hasServiceAccess = Boolean(serviceCampus?.length)

  const pages = (isServiceProvider ? providerMenu : schoolMenu)
    .filter(item => item.path !== '/schools' || hasServiceAccess)
    .filter(item => item.path !== '/mentorship/my-logs' || shouldShowMentorshipMenu)
  // const shouldShowServiceProviderSettings = isServiceProvider
  // const shouldShowSchoolSettings = academicYear?.id && permissions?.['setting.setting']

  const schoolName = campus?.name || school?.name || district?.name || ''

  const maxMenuHeight = `calc(100vh - ${MENU_HEIGHTS.TOOLBAR +
    MENU_HEIGHTS.HEADER +
    MENU_HEIGHTS.FOOTER +
    MENU_HEIGHTS.PADDING
    }px)`

  const renderMenuItems = menuItems =>
    menuItems
      .filter(page => hasPermission(page.permission))
      .map(page => (
        <MenuItem
          data-menu={page.title}
          key={page.path}
          page={page}
          location={location}
          isMenuCollapsed={isMenuCollapsed}
        />
      ))

  const handleLogout = async () => {
    await dispatch(logout())
    navigate('/auth/login')
  }

  const handleNavigateHome = () => navigate('/')

  return (
    <Box sx={{ position: 'relative' }}>
      <Grid
        data-component="AppMenu"
        container
        direction="column"
        sx={{
          height: '100vh',
          justifyContent: 'space-between',
          pl: isMenuCollapsed ? 0 : 1,
          pr: 1,
          overflow: 'visible',
        }}
      >
        <Grid item className={`appMenu ${isMenuCollapsed ? 'collapsed' : ''}`}>
          <Box display="flex" flexDirection="column" sx={{ height: '100vh' }}>
            {/* Logo */}
            <Box>
              <Toolbar sx={{ flexDirection: 'column', alignItems: 'center' }}>
                <Box sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  pt: 1
                }}>
                  <Logo
                    isMenuCollapsed={isMenuCollapsed}
                    logos={logos}
                    onClick={handleNavigateHome}
                  />
                  {!isMenuCollapsed && !isServiceProvider && (
                    <SchoolName name={schoolName} onClick={handleNavigateHome} />
                  )}
                </Box>
              </Toolbar>

              <Divider color="white" />

              {/* Main Menu Items */}
              <Box sx={{ overflow: 'auto', maxHeight: maxMenuHeight }}>
                <List>
                  {isParent && studentList && (
                    <>
                      <ParentStudents
                        studentList={studentList}
                        location={location}
                        MenuItem={MenuItem}
                      />
                      <Divider color="white" />
                    </>
                  )}

                  {renderMenuItems(pages)}

                  {isStudentOrParent && renderMenuItems(studentMenu)}
                </List>

                {shouldShowAcademicMenu && (
                  <Fragment>
                    <Divider color="white" />
                    <List>{renderMenuItems(academicMenu)}</List>
                  </Fragment>
                )}
              </Box>

              <Divider color="white" />
            </Box>

            {/* Bottom Menu Items */}
            <Box style={{ marginTop: 'auto', overflow: 'auto' }}>
              <Grid item sx={{ paddingBottom: '40px' }}>
                {isStaff && hasPermission('miscellaneous.notification') && (
                  <ActionMenuItem
                    icon={NotificationIcon}
                    label="Notification"
                    onClick={() => navigate('/notification')}
                    isMenuCollapsed={isMenuCollapsed}
                    dataMenu="Notification"
                  />
                )}

                {hasPermission('setting.setting') &&
                  <ActionMenuItem
                    icon={SettingsIcon}
                    label="Settings"
                    onClick={() => navigate('/settings')}
                    isMenuCollapsed={isMenuCollapsed}
                    dataMenu="Settings"
                  />
                }

                {/* {shouldShowSchoolSettings && (
                  <ActionMenuItem
                    icon={SettingsIcon}
                    label="Settings"
                    onClick={() => navigate('/settings_')}
                    isMenuCollapsed={isMenuCollapsed}
                    dataMenu="Settings"
                  />
                )} */}

                {isServiceProvider && hasPermission('setting.permission') && (
                  <ActionMenuItem
                    icon={PermissionIcon}
                    label="Permissions"
                    onClick={() => navigate('/permission')}
                    isMenuCollapsed={isMenuCollapsed}
                    dataMenu="Permissions"
                  />
                )}

                <ActionMenuItem
                  icon={LogoutIcon}
                  label="Logout"
                  onClick={handleLogout}
                  isMenuCollapsed={isMenuCollapsed}
                  dataMenu="Logout"
                />

                {isParent && <JotformFeedback />}
              </Grid>
            </Box>
          </Box>
        </Grid>
      </Grid>
    </Box>
  )
}

AppMenu.propTypes = {
  isMenuCollapsed: PropTypes.bool.isRequired,
}
